import React, { useState } from "react";
import { Segmented } from "antd5";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import { TrackingProvider } from "lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";
import { SaveToLists } from "./lists/SaveToLists";
import { SaveToOpportunity } from "./opportunity/SaveToOpportunity";
import { EntityType, SharedSaveProps } from "./types";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  savedCount?: number;
} & SharedSaveProps;

const hasList: Record<EntityType, boolean> = {
  buyer: true,
  supplier: true,
  notice: true,
  framework: false,
  document: false,
  contact: false,
};

export const showListWithOpp: Record<EntityType, boolean> = {
  ...hasList,
  notice: false, // We want to nudge users towards using opportunities rather than lists for save functionality. Hence, even though notice lists exist, they are not shown in this save drawer alongside opportunities .
};

export function SaveToDrawer({
  isOpen,
  onOpenChange,
  entityType,
  entityIds,
  entityData,
  defaultOpportunityValues,
  savedCount,
}: Props) {
  const [selectedTab, setSelectedTab] = useState<"opportunity" | "list">(
    entityType === "buyer" || entityType === "supplier" ? "list" : "opportunity",
  );
  const [creation, setCreation] = useState(false);

  const showTabs = showListWithOpp[entityType] && !creation;

  const showOpportunityContent = selectedTab === "opportunity" || !showListWithOpp[entityType];

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} width={486} padding={24}>
      <TrackingProvider data={{ "Data type": entityType.toUpperCase() }}>
        <Flex gap={32} height="100%">
          <Flex column height="100%" gap={24} width="440px">
            <Flex justifyContent="space-between" alignItems="flex-start">
              {creation ? (
                <Button
                  as="button"
                  buttonType="secondary"
                  buttonStyle="default"
                  icon="arrowNarrowLeft"
                  onClick={() => setCreation(false)}
                />
              ) : (
                <Text h3>
                  {showOpportunityContent ? "Save to an opportunity" : "Save to a list"}
                </Text>
              )}
              <Button
                as="button"
                buttonType="secondary"
                buttonStyle="default"
                icon="xClose"
                onClick={() => onOpenChange(false)}
              />
            </Flex>
            <Flex column height="calc(100% - 48px)" gap={24}>
              {showTabs && (
                <Segmented
                  options={[
                    { value: "opportunity", label: "Opportunity" },
                    { value: "list", label: "List" },
                  ]}
                  onChange={(value: "opportunity" | "list") => setSelectedTab(value)}
                  value={selectedTab}
                  block
                />
              )}
              <Flex column height={showTabs ? "calc(100% - 56px)" : "100%"}>
                {showOpportunityContent ? (
                  <SaveToOpportunity
                    entityType={entityType}
                    entityIds={entityIds}
                    entityData={entityData}
                    defaultOpportunityValues={defaultOpportunityValues}
                    creation={creation}
                    setCreation={setCreation}
                    savedCount={savedCount}
                  />
                ) : (
                  <SaveToLists
                    entityType={entityType}
                    entityIds={entityIds}
                    creation={creation}
                    setCreation={setCreation}
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </TrackingProvider>
    </Drawer>
  );
}
